// src/hooks/useAuth.tsx
import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { login as apiLogin, getMe, setAuthSession, clearAuthSession, getAuthUser, getAuthToken, setActiveBranchId } from '../api';
import { queryClient } from '../lib/queryClient';
import { invalidateBranchScoped } from '../lib/queryKeys';
import type { SessionUser } from '../lib/api/session';

export type LoginOutcome =
  | { stage: 'session' }
  | { stage: 'verify' | 'setup'; mfaToken: string };

export interface AuthContextValue {
  user: SessionUser | null;
  setUser: (user: SessionUser | null | ((prev: SessionUser | null) => SessionUser | null)) => void;
  checking: boolean;
  /** 'session' = signed in; otherwise two-step login needs a code ('verify') or a first-time setup ('setup'). */
  login: (username: string, password: string) => Promise<LoginOutcome>;
  /** Finishes a two-step sign-in with the session /auth/mfa/verify or /enable returned. */
  completeSignIn: (token: string, user: SessionUser) => void;
  logout: () => void;
  switchBranch: (branchId: number | string) => void;
  /** Bumped on every branch switch — see switchBranch's own comment. */
  branchVersion: number;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser]       = useState<SessionUser | null>(null);
  const [checking, setChecking] = useState(true);
  // Bumped on every branch switch. Components that show branch-scoped data
  // (queue, wards, inventory, bills, …) include this in their fetch
  // useEffect's dependency array so switching branch refetches them in
  // place, without the earlier full-page-reload approach.
  const [branchVersion, setBranchVersion] = useState(0);

  useEffect(() => {
    if (!getAuthToken()) { setChecking(false); return; }
    // Trust the cached user for instant render, but confirm against the server
    // (catches a revoked/deactivated account or expired token).
    setUser(getAuthUser());
    getMe()
      .then(setUser)
      .catch(() => { clearAuthSession(); setUser(null); })
      .finally(() => setChecking(false));
  }, []);

  const completeSignIn = (token: string, u: SessionUser) => {
    setAuthSession(token, u);
    setUser(u);
  };

  const login = async (username: string, password: string): Promise<LoginOutcome> => {
    const r = await apiLogin(username, password);
    if (r.mfa_required) return { stage: 'verify', mfaToken: r.mfa_token };
    if (r.mfa_setup_required) return { stage: 'setup', mfaToken: r.mfa_token };
    completeSignIn(r.token, r.user);
    return { stage: 'session' };
  };

  const logout = () => {
    clearAuthSession();
    setUser(null);
  };

  // Switching branch changes what nearly every screen's data means (a
  // different queue, different beds, different inventory). Rather than a
  // full reload, this updates the locally-cached user's active_branch (so
  // the switcher itself reflects the change immediately) and bumps
  // branchVersion, which every branch-scoped screen's fetch effect depends
  // on — each refetches in place instead of the whole app remounting.
  const switchBranch = (branchId: number | string) => {
    setActiveBranchId(Number(branchId));
    setUser(u => {
      if (!u) return u;
      const branch = u.branches?.find(b => b.id === Number(branchId));
      return branch ? { ...u, active_branch: branch } : u;
    });
    setBranchVersion(v => v + 1);
    // Evicts cached branch-scoped React Query data (see src/lib/queryKeys.ts
    // for why this removes rather than refetches). branchVersion above
    // remains the primary mechanism — every migrated screen's data hook
    // depends on it, which is what forces a re-render with a freshly-scoped
    // key — this is a memory-hygiene backstop, not a legacy screen's only
    // path to fresh data (those still key off branchVersion directly too).
    invalidateBranchScoped(queryClient);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, checking, login, completeSignIn, logout, switchBranch, branchVersion }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
